import apiClient from "./apiClient";

/**
 * Fetches keyframes for a given footage
 * @param {string} footageUuid - The UUID of the footage (from inspection.footage.id)
 * @returns {Promise<Array>} Array of keyframe objects
 */
export const fetchKeyframes = async (footageUuid) => {
  const response = await apiClient.get(`/footage/${footageUuid}/keyframes`);
  return response.data;
};

/**
 * Fetches observations for a given inspection
 * @param {string} inspectionUuid - The UUID of the inspection
 * @returns {Promise<Array>} Array of observation objects
 */
export const fetchObservations = async (inspectionUuid) => {
  const response = await apiClient.get(
    `/inspections/${inspectionUuid}/observations`,
  );
  return response.data;
};

/**
 * Creates a new observation for an inspection
 * @param {string} inspectionUuid - The UUID of the inspection
 * @param {Object} payload - Observation object to create
 * @returns {Promise<Object>} Response with updated inspection grades
 */
export const createObservation = async (inspectionUuid, payload) => {
  const response = await apiClient.post(
    `/inspections/${inspectionUuid}/observations`,
    payload,
  );
  return response.data;
};

/**
 * Patches a single observation
 * @param {string} observationId - The UUID of the observation
 * @param {Object} payload - The observation fields to update
 * @returns {Promise<Object>} Updated observation response with grades
 */
export const patchObservation = async (observationId, payload) => {
  // Create a copy to avoid mutating the caller's payload object
  const requestPayload = { ...payload };

  // Transform payload: convert empty strings to null
  Object.keys(requestPayload).forEach((key) => {
    if (requestPayload[key] === "") {
      requestPayload[key] = null;
    }
  });

  // Set default values for quantity units (handles null and undefined)
  if (requestPayload?.quantity1_units == null) {
    requestPayload.quantity1_units = "-";
  }
  if (requestPayload?.quantity2_units == null) {
    requestPayload.quantity2_units = "-";
  }

  const response = await apiClient.patch(
    `/inspections/observations/${observationId}`,
    requestPayload,
  );
  return response.data;
};

/**
 * Patches an observed feature
 * @param {string} observedFeatureId - The UUID of the observed feature
 * @param {Object} payload - The feature fields to update
 * @returns {Promise<Object>} Updated feature response with grades
 */
export const patchObservedFeature = async (observedFeatureId, payload) => {
  const response = await apiClient.patch(
    `/inspections/observations/features/${observedFeatureId}`,
    payload,
  );
  return response.data;
};

/**
 * Patches a keyframe
 * @param {string} keyframeId - The UUID of the keyframe
 * @param {Object} payload - The keyframe fields to update (chainage, atJoint, isHidden)
 * @returns {Promise<Object>} Updated keyframe response
 */
export const patchKeyframe = async (keyframeId, payload) => {
  const response = await apiClient.patch(`/keyframes/${keyframeId}`, payload);
  return response.data;
};

/**
 * Deletes an observation
 * @param {string} observationId - The UUID of the observation to delete
 * @returns {Promise<void>}
 */
export const deleteObservation = async (observationId) => {
  await apiClient.delete(`/inspections/observations/${observationId}`);
};
